import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import {
  BankAccountOverviewRow,
  CashboxOverviewRow,
  CounterpartyOverviewRow,
  FinanceApiService,
  PaymentChequeOverviewRow,
  PaymentReceiptOverviewFilters,
  PaymentReceiptOverviewPage,
  PaymentReceiptOverviewTotals,
  PosTerminalOverviewRow,
  ReceiptChequeOverviewRow
} from './finance-api.service';

type OverviewTab = 'banks' | 'cashboxes' | 'pos' | 'counterparties' | 'receiptCheques' | 'paymentCheques';

@Component({
  standalone: true,
  selector: 'ain-finance-payments-daily-overview',
  imports: [
    AinPageComponent,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    JalaliDateInputComponent,
    AinDatePipe,
    DecimalPipe
  ],
  template: `
    <ain-page [title]="t('finance.dailyOverview.title')" [description]="t('finance.dailyOverview.description')">
      <section class="card">
        <div class="tabs">
          @for (tab of tabs; track tab.id) {
            <button
              type="button"
              class="tab-btn"
              [class.active]="activeTab() === tab.id"
              (click)="selectTab(tab.id)">
              {{ t(tab.labelKey) }}
            </button>
          }
        </div>

        <div class="filters">
          @if (activeTab() === 'banks' || activeTab() === 'pos' || activeTab() === 'receiptCheques' || activeTab() === 'paymentCheques') {
            <input pInputText type="text" [(ngModel)]="searchBankName" [placeholder]="t('finance.dailyOverview.fields.bankName')" />
            <input pInputText type="text" [(ngModel)]="searchBranchCode" [placeholder]="t('finance.dailyOverview.fields.branchCode')" />
          }
          @if (activeTab() === 'banks' || activeTab() === 'pos') {
            <input pInputText type="text" [(ngModel)]="searchAccountNumber" [placeholder]="t('finance.dailyOverview.fields.accountNumber')" />
          }
          @if (activeTab() === 'cashboxes') {
            <input pInputText type="text" [(ngModel)]="searchName" [placeholder]="t('finance.dailyOverview.fields.cashboxName')" />
            <input pInputText type="text" [(ngModel)]="searchAccountCode" [placeholder]="t('finance.dailyOverview.fields.accountCode')" />
          }
          @if (activeTab() === 'pos') {
            <input pInputText type="text" [(ngModel)]="searchDeviceSerial" [placeholder]="t('finance.dailyOverview.fields.deviceSerial')" />
          }
          @if (activeTab() === 'counterparties') {
            <input pInputText type="text" [(ngModel)]="searchName" [placeholder]="t('finance.dailyOverview.fields.counterpartyName')" />
            <input pInputText type="text" [(ngModel)]="searchCode" [placeholder]="t('finance.dailyOverview.fields.counterpartyCode')" />
          }
          @if (activeTab() === 'receiptCheques' || activeTab() === 'paymentCheques') {
            <input pInputText type="text" [(ngModel)]="searchChequeNumber" [placeholder]="t('finance.dailyOverview.fields.chequeNumber')" />
            @if (activeTab() === 'receiptCheques') {
              <input pInputText type="text" [(ngModel)]="searchPayee" [placeholder]="t('finance.dailyOverview.fields.payee')" />
              <input pInputText type="text" [(ngModel)]="searchHolderNationalCode" [placeholder]="t('finance.dailyOverview.fields.holderNationalCode')" />
            } @else {
              <input pInputText type="text" [(ngModel)]="searchReceiver" [placeholder]="t('finance.dailyOverview.fields.receiver')" />
              <input pInputText type="text" [(ngModel)]="searchReceiverNationalCode" [placeholder]="t('finance.dailyOverview.fields.receiverNationalCode')" />
            }
          }

          @if (activeTab() !== 'receiptCheques' && activeTab() !== 'paymentCheques') {
            <ain-date-input [(ngModel)]="startDate" [placeholder]="t('finance.dailyOverview.fields.startDate')" />
            <ain-date-input [(ngModel)]="endDate" [placeholder]="t('finance.dailyOverview.fields.endDate')" />
          } @else {
            <ain-date-input [(ngModel)]="startDueDate" [placeholder]="t('finance.dailyOverview.fields.startDate')" />
            <ain-date-input [(ngModel)]="endDueDate" [placeholder]="t('finance.dailyOverview.fields.endDate')" />
          }

          @if (showBalanceFilters()) {
            <input pInputText type="number" [(ngModel)]="minBalance" [placeholder]="t('finance.dailyOverview.fields.minBalance')" />
            <input pInputText type="number" [(ngModel)]="maxBalance" [placeholder]="t('finance.dailyOverview.fields.maxBalance')" />
          }
          @if (activeTab() === 'pos' || activeTab() === 'receiptCheques' || activeTab() === 'paymentCheques') {
            <input pInputText type="number" [(ngModel)]="minAmount" [placeholder]="t('finance.dailyOverview.fields.minAmount')" />
            <input pInputText type="number" [(ngModel)]="maxAmount" [placeholder]="t('finance.dailyOverview.fields.maxAmount')" />
          }

          <button pButton type="button" [label]="t('finance.dailyOverview.apply')" (click)="applySearch()"></button>
          <button pButton type="button" [label]="t('finance.dailyOverview.clear')" severity="secondary" (click)="clearFilters()"></button>
          <button pButton type="button" [label]="t('finance.dailyOverview.refresh')" severity="secondary" (click)="load()"></button>
        </div>

        @if (activeTab() === 'banks') {
          <p-table [value]="bankRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.bankName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.branchCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.accountNumber') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receivedAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.paidAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.balance') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.bankName }}</td>
                <td>{{ row.branchCode || '-' }}</td>
                <td>{{ row.accountNumber || '-' }}</td>
                <td [class.negative]="row.receivedAmount < 0">{{ row.receivedAmount | number:'1.0-0' }}</td>
                <td [class.negative]="row.paidAmount < 0">{{ row.paidAmount | number:'1.0-0' }}</td>
                <td [class.negative]="row.balance < 0">{{ row.balance | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="3"><strong>{{ t('finance.dailyOverview.totals.received') }} / {{ t('finance.dailyOverview.totals.paid') }} / {{ t('finance.dailyOverview.totals.balance') }}</strong></td>
                <td [class.negative]="totals().receivedTotal < 0"><strong>{{ totals().receivedTotal | number:'1.0-0' }}</strong></td>
                <td [class.negative]="totals().paidTotal < 0"><strong>{{ totals().paidTotal | number:'1.0-0' }}</strong></td>
                <td [class.negative]="totals().balanceTotal < 0"><strong>{{ totals().balanceTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (activeTab() === 'cashboxes') {
          <p-table [value]="cashboxRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.cashboxName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.accountCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receivedAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.paidAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.balance') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.cashboxName }}</td>
                <td>{{ row.accountCode || '-' }}</td>
                <td [class.negative]="row.receivedAmount < 0">{{ row.receivedAmount | number:'1.0-0' }}</td>
                <td [class.negative]="row.paidAmount < 0">{{ row.paidAmount | number:'1.0-0' }}</td>
                <td [class.negative]="row.balance < 0">{{ row.balance | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="2"><strong>{{ t('finance.dailyOverview.totals.received') }} / {{ t('finance.dailyOverview.totals.paid') }} / {{ t('finance.dailyOverview.totals.balance') }}</strong></td>
                <td><strong>{{ totals().receivedTotal | number:'1.0-0' }}</strong></td>
                <td><strong>{{ totals().paidTotal | number:'1.0-0' }}</strong></td>
                <td [class.negative]="totals().balanceTotal < 0"><strong>{{ totals().balanceTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (activeTab() === 'pos') {
          <p-table [value]="posRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.bankName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.branchCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.accountNumber') }}</th>
                <th>{{ t('finance.dailyOverview.fields.deviceSerial') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receivedAmount') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.bankName }}</td>
                <td>{{ row.branchCode || '-' }}</td>
                <td>{{ row.accountNumber || '-' }}</td>
                <td>{{ row.deviceSerialNumber || '-' }}</td>
                <td>{{ row.receivedAmount | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="4"><strong>{{ t('finance.dailyOverview.totals.received') }}</strong></td>
                <td><strong>{{ totals().receivedTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (activeTab() === 'counterparties') {
          <p-table [value]="counterpartyRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.counterpartyName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.counterpartyCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receivedAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.paidAmount') }}</th>
                <th>{{ t('finance.dailyOverview.fields.balance') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.name }}</td>
                <td>{{ row.code || '-' }}</td>
                <td>{{ row.receivedAmount | number:'1.0-0' }}</td>
                <td>{{ row.paidAmount | number:'1.0-0' }}</td>
                <td [class.negative]="row.balance < 0">{{ row.balance | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="5">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="2"><strong>{{ t('finance.dailyOverview.totals.received') }} / {{ t('finance.dailyOverview.totals.paid') }} / {{ t('finance.dailyOverview.totals.balance') }}</strong></td>
                <td><strong>{{ totals().receivedTotal | number:'1.0-0' }}</strong></td>
                <td><strong>{{ totals().paidTotal | number:'1.0-0' }}</strong></td>
                <td [class.negative]="totals().balanceTotal < 0"><strong>{{ totals().balanceTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (activeTab() === 'receiptCheques') {
          <p-table [value]="receiptChequeRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.bankName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.branchCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.chequeNumber') }}</th>
                <th>{{ t('finance.dailyOverview.fields.payee') }}</th>
                <th>{{ t('finance.dailyOverview.fields.holderNationalCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.dueDate') }}</th>
                <th>{{ t('finance.dailyOverview.fields.chequeAmount') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.bankName }}</td>
                <td>{{ row.branchCode || '-' }}</td>
                <td>{{ row.chequeNumber }}</td>
                <td>{{ row.payee || '-' }}</td>
                <td>{{ row.holderNationalCode || '-' }}</td>
                <td>{{ row.dueDateUtc | ainDate }}</td>
                <td>{{ row.chequeAmount | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="7">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="6"><strong>{{ t('finance.dailyOverview.totals.received') }}</strong></td>
                <td><strong>{{ totals().receivedTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (activeTab() === 'paymentCheques') {
          <p-table [value]="paymentChequeRows()" [paginator]="true" [rows]="pageSize" [totalRecords]="totalCount()" [lazy]="true" (onLazyLoad)="onPageChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.dailyOverview.fields.bankName') }}</th>
                <th>{{ t('finance.dailyOverview.fields.branchCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.chequeNumber') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receiver') }}</th>
                <th>{{ t('finance.dailyOverview.fields.receiverNationalCode') }}</th>
                <th>{{ t('finance.dailyOverview.fields.dueDate') }}</th>
                <th>{{ t('finance.dailyOverview.fields.chequeAmount') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.bankName }}</td>
                <td>{{ row.branchCode || '-' }}</td>
                <td>{{ row.chequeNumber }}</td>
                <td>{{ row.receiver || '-' }}</td>
                <td>{{ row.receiverNationalCode || '-' }}</td>
                <td>{{ row.dueDateUtc | ainDate }}</td>
                <td>{{ row.chequeAmount | number:'1.0-0' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="7">{{ t('finance.dailyOverview.noRecords') }}</td></tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr class="totals-row">
                <td colspan="6"><strong>{{ t('finance.dailyOverview.totals.paid') }}</strong></td>
                <td><strong>{{ totals().paidTotal | number:'1.0-0' }}</strong></td>
              </tr>
            </ng-template>
          </p-table>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; display: grid; gap: 12px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
    .tab-btn { border: 1px solid #e5e7eb; background: #f9fafb; border-radius: 8px; padding: 8px 12px; cursor: pointer; font-weight: 600; }
    .tab-btn.active { background: #eff6ff; border-color: #93c5fd; color: #1d4ed8; }
    .filters { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    .totals-row td { background: #f9fafb; }
    .negative { color: #b91c1c; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
  `]
})
export class FinancePaymentsDailyOverviewComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly tabs: { id: OverviewTab; labelKey: string }[] = [
    { id: 'banks', labelKey: 'finance.dailyOverview.tabs.banks' },
    { id: 'cashboxes', labelKey: 'finance.dailyOverview.tabs.cashboxes' },
    { id: 'pos', labelKey: 'finance.dailyOverview.tabs.pos' },
    { id: 'counterparties', labelKey: 'finance.dailyOverview.tabs.counterparties' },
    { id: 'receiptCheques', labelKey: 'finance.dailyOverview.tabs.receiptCheques' },
    { id: 'paymentCheques', labelKey: 'finance.dailyOverview.tabs.paymentCheques' }
  ];

  readonly activeTab = signal<OverviewTab>('banks');
  readonly bankRows = signal<BankAccountOverviewRow[]>([]);
  readonly cashboxRows = signal<CashboxOverviewRow[]>([]);
  readonly posRows = signal<PosTerminalOverviewRow[]>([]);
  readonly counterpartyRows = signal<CounterpartyOverviewRow[]>([]);
  readonly receiptChequeRows = signal<ReceiptChequeOverviewRow[]>([]);
  readonly paymentChequeRows = signal<PaymentChequeOverviewRow[]>([]);
  readonly totals = signal<PaymentReceiptOverviewTotals>({ receivedTotal: 0, paidTotal: 0, balanceTotal: 0 });
  readonly totalCount = signal(0);
  readonly errorMessage = signal('');

  page = 1;
  pageSize = 15;

  searchBankName = '';
  searchBranchCode = '';
  searchAccountNumber = '';
  searchName = '';
  searchAccountCode = '';
  searchCode = '';
  searchDeviceSerial = '';
  searchChequeNumber = '';
  searchPayee = '';
  searchHolderNationalCode = '';
  searchReceiver = '';
  searchReceiverNationalCode = '';
  startDate = '';
  endDate = '';
  startDueDate = '';
  endDueDate = '';
  minBalance: number | null = null;
  maxBalance: number | null = null;
  minAmount: number | null = null;
  maxAmount: number | null = null;

  ngOnInit(): void {
    this.load();
  }

  selectTab(tab: OverviewTab): void {
    this.activeTab.set(tab);
    this.page = 1;
    this.load();
  }

  showBalanceFilters(): boolean {
    const tab = this.activeTab();
    return tab === 'banks' || tab === 'cashboxes' || tab === 'counterparties';
  }

  applySearch(): void {
    this.page = 1;
    this.load();
  }

  clearFilters(): void {
    this.searchBankName = '';
    this.searchBranchCode = '';
    this.searchAccountNumber = '';
    this.searchName = '';
    this.searchAccountCode = '';
    this.searchCode = '';
    this.searchDeviceSerial = '';
    this.searchChequeNumber = '';
    this.searchPayee = '';
    this.searchHolderNationalCode = '';
    this.searchReceiver = '';
    this.searchReceiverNationalCode = '';
    this.startDate = '';
    this.endDate = '';
    this.startDueDate = '';
    this.endDueDate = '';
    this.minBalance = null;
    this.maxBalance = null;
    this.minAmount = null;
    this.maxAmount = null;
    this.page = 1;
    this.load();
  }

  onPageChange(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.pageSize;
    const first = event.first ?? 0;
    this.pageSize = rows;
    this.page = Math.floor(first / rows) + 1;
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    const filters = this.buildFilters();

    const handlePage = <T>(page: PaymentReceiptOverviewPage<T>, applyRows: (rows: T[]) => void): void => {
      applyRows(page.items);
      this.totalCount.set(page.totalCount);
      this.totals.set(page.totals);
    };

    const onError = (): void => {
      this.bankRows.set([]);
      this.cashboxRows.set([]);
      this.posRows.set([]);
      this.counterpartyRows.set([]);
      this.receiptChequeRows.set([]);
      this.paymentChequeRows.set([]);
      this.totalCount.set(0);
      this.totals.set({ receivedTotal: 0, paidTotal: 0, balanceTotal: 0 });
      this.errorMessage.set(this.t('finance.dailyOverview.messages.loadFailed'));
    };

    switch (this.activeTab()) {
      case 'banks':
        this.api.getPaymentReceiptOverviewBankAccounts(filters).subscribe({
          next: page => handlePage(page, rows => this.bankRows.set(rows)),
          error: onError
        });
        break;
      case 'cashboxes':
        this.api.getPaymentReceiptOverviewCashboxes(filters).subscribe({
          next: page => handlePage(page, rows => this.cashboxRows.set(rows)),
          error: onError
        });
        break;
      case 'pos':
        this.api.getPaymentReceiptOverviewPosTerminals(filters).subscribe({
          next: page => handlePage(page, rows => this.posRows.set(rows)),
          error: onError
        });
        break;
      case 'counterparties':
        this.api.getPaymentReceiptOverviewCounterparties(filters).subscribe({
          next: page => handlePage(page, rows => this.counterpartyRows.set(rows)),
          error: onError
        });
        break;
      case 'receiptCheques':
        this.api.getPaymentReceiptOverviewReceiptCheques(filters).subscribe({
          next: page => handlePage(page, rows => this.receiptChequeRows.set(rows)),
          error: onError
        });
        break;
      case 'paymentCheques':
        this.api.getPaymentReceiptOverviewPaymentCheques(filters).subscribe({
          next: page => handlePage(page, rows => this.paymentChequeRows.set(rows)),
          error: onError
        });
        break;
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private buildFilters(): PaymentReceiptOverviewFilters {
    const tab = this.activeTab();
    const filters: PaymentReceiptOverviewFilters = {
      page: this.page,
      pageSize: this.pageSize
    };

    if (tab === 'banks' || tab === 'pos' || tab === 'receiptCheques' || tab === 'paymentCheques') {
      filters.searchBankName = this.searchBankName;
      filters.searchBranchCode = this.searchBranchCode;
    }
    if (tab === 'banks' || tab === 'pos') {
      filters.searchAccountNumber = this.searchAccountNumber;
    }
    if (tab === 'cashboxes') {
      filters.searchName = this.searchName;
      filters.searchAccountCode = this.searchAccountCode;
    }
    if (tab === 'counterparties') {
      filters.searchName = this.searchName;
      filters.searchCode = this.searchCode;
    }
    if (tab === 'pos') {
      filters.searchDeviceSerial = this.searchDeviceSerial;
    }
    if (tab === 'receiptCheques') {
      filters.searchChequeNumber = this.searchChequeNumber;
      filters.searchPayee = this.searchPayee;
      filters.searchHolderNationalCode = this.searchHolderNationalCode;
      filters.startDueDateUtc = this.toIsoDate(this.startDueDate);
      filters.endDueDateUtc = this.toIsoDate(this.endDueDate);
    } else if (tab === 'paymentCheques') {
      filters.searchChequeNumber = this.searchChequeNumber;
      filters.searchReceiver = this.searchReceiver;
      filters.searchReceiverNationalCode = this.searchReceiverNationalCode;
      filters.startDueDateUtc = this.toIsoDate(this.startDueDate);
      filters.endDueDateUtc = this.toIsoDate(this.endDueDate);
    } else {
      filters.startDateUtc = this.toIsoDate(this.startDate);
      filters.endDateUtc = this.toIsoDate(this.endDate);
    }

    if (this.showBalanceFilters()) {
      if (this.minBalance !== null && this.minBalance !== undefined && !Number.isNaN(this.minBalance)) {
        filters.minBalance = this.minBalance;
      }
      if (this.maxBalance !== null && this.maxBalance !== undefined && !Number.isNaN(this.maxBalance)) {
        filters.maxBalance = this.maxBalance;
      }
    }

    if (tab === 'pos' || tab === 'receiptCheques' || tab === 'paymentCheques') {
      if (this.minAmount !== null && this.minAmount !== undefined && !Number.isNaN(this.minAmount)) {
        filters.minAmount = this.minAmount;
      }
      if (this.maxAmount !== null && this.maxAmount !== undefined && !Number.isNaN(this.maxAmount)) {
        filters.maxAmount = this.maxAmount;
      }
    }

    return filters;
  }

  private toIsoDate(value: string): string | undefined {
    if (!value?.trim()) {
      return undefined;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }
}
